import React, {
  useState,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Tables, Spinner } from '../../../../../Components';
import {
  returnPropsByPermissions,
  showError,
  showSuccess,
} from '../../../../../Helper';
import {
  DeleteVendorAgreementAPI,
} from '../../../../../Services';
import { TableActions } from '../../../../../Enums';
import { MessageConfirmDialog } from '../../../../../SharedComponents';
import { AgreementDetailsManagementDialog } from './AgreementDetailsManagement';
import { DevelopersPermissions } from '../../../../../Permissions';

export const AgreementDetailsTable = ({
  parentTranslationPath,
  data,
  filter,
  setFilter,
  reloadData,
}) => {
  const { t } = useTranslation(parentTranslationPath, 'Shared');
  const [isLoading, setIsLoading] = useState({ info: false });
  const [activeItem, setActiveItem] = useState(null);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [isMissingInfoMSGOpen, setIsMissingInfoMSGOpen] = useState(false);

  const tableActionClicked = useCallback(
    (actionEnum, item, focusedRow, event) => {
      event.stopPropagation();
      event.preventDefault();
      setActiveItem(item);

      if (actionEnum === TableActions.delete.key) {
        setIsMissingInfoMSGOpen(true);
      } else if (actionEnum === TableActions.editText.key) {
        setIsOpenEditDialog(true);
      }
    },
    []
  );

  const onPageIndexChanged = (pageIndex) => {
    setFilter((item) => ({ ...item, pageIndex }));
  };
  const onPageSizeChanged = (pageSize) => {
    setFilter((item) => ({ ...item, pageIndex: 0, pageSize }));
  };

  const getActionTableWithPermissions = () => {
    const list = [];
    if (
      returnPropsByPermissions(
        DevelopersPermissions.EditAgreementDetails.permissionsId
      )
    )
      list.push({
        enum: TableActions.editText.key,
        title: t(`Shared:edit`),
      });
    list.push({
      enum: TableActions.delete.key,
      title: t(`Shared:delete`),
    });
    return list;
  };

  const deleteHandler = useCallback(async () => {
    try {
      setIsLoading((item) => ({ ...item, delete: true }));

      await DeleteVendorAgreementAPI(activeItem.vendorAgreementId);
      showSuccess(t(`delete-agreement-detail-successfully`));
      setIsMissingInfoMSGOpen(false);
      setActiveItem(null);
      reloadData();
    } catch (error) {
      showError(t(`failed-to-delete-agreement-detail`));
    } finally {
      setIsLoading((item) => ({ ...item, delete: false }));
    }
  }, [activeItem]);

  return (
    <div className='d-flex-column mt-2'>
      <Spinner isActive={isLoading.info || isLoading.delete} isAbsolute />
      <Tables
        data={data.result}
        headerData={[
          {
            label: `agreement-status`,
            input: 'agreementStatus',
            component: (row) => row.agreementStatus?.agreementStatusName,
          },
          {
            label: `agreement-start-date`,
            input: 'contractStartDate',
            isDate: true,
          },
          {
            label: `agreement-duration-months`,
            input: 'contractDuration',
          },
          {
            label: `agreement-end-date`,
            input: 'contractExpiryDate',
            isDate: true,
          },
          {
            label: `psi_commission`,
            input: 'psiCommission',
          },
        ]}
        onPageIndexChanged={onPageIndexChanged}
        onPageSizeChanged={onPageSizeChanged}
        defaultActions={getActionTableWithPermissions()}
        actionsOptions={{
          onActionClicked: tableActionClicked,
        }}
        activePage={filter.pageIndex}
        parentTranslationPath={parentTranslationPath}
        itemsPerPage={filter.pageSize}
        totalItems={data?.totalCount}
      />
      {isMissingInfoMSGOpen && activeItem && (
        <MessageConfirmDialog
          isDialogOpen={isMissingInfoMSGOpen}
          theme='warning'
          content={{
            heading: 'delete-agreement-details',
            body:
              t(`are-you-sure-you-want-to-delete-delete-agreement-details-id`) +
              ' ' +
              activeItem?.vendorAgreementId,
          }}
          confirmButton={{
            label: t(`confirm`),
            handler: deleteHandler,
          }}
          cancelButton={{
            label: t(`discard`),
            handler: () => {
              setIsMissingInfoMSGOpen(false);
            },
          }}
          parentTranslationPath={parentTranslationPath}
        />
      )}
      {isOpenEditDialog && activeItem && (
        <AgreementDetailsManagementDialog
          parentTranslationPath={parentTranslationPath}
          isOpen={isOpenEditDialog}
          onClose={() => {
            setIsOpenEditDialog(false);
            setActiveItem(null);
          }}
          onSave={() => {
            setIsOpenEditDialog(false);
            setActiveItem(null);
            reloadData();
          }}
          agreementDetailsValues={activeItem}
        />
      )}
    </div>
  );
};
